import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { noop, throttle } from 'lodash';
import { reportError } from '@atlassian/help-center-common-util/sentry';

export interface FrameLoaderProps {
    src: string;
    sameOriginResize: boolean;
    resizePoll: boolean;
    resizePollInterval: number;
    title: string;
    onIFrameLoaded?: () => void;
}

export class IFrameLoader extends React.Component<FrameLoaderProps> {
    static defaultProps = {
        sameOriginResize: false,
        resizePoll: false,
        resizePollInterval: 500,
    };

    iFrameRef: HTMLIFrameElement | null;
    removeResizeListener = noop;
    removeResizePolling = noop;

    resizeIFrameHeight = throttle(() => {
        const iframe = this.tryGetIframe();
        if (iframe) {
            // clientHeight has the correct content height – scrollHeight only ever increases on Safari
            //       that is to say, it doesn't reflect the content height but the container height, which we're setting here
            // iframe.height = ... sets the container height so the content height is left unaffected, so decreases can be detected
            iframe.ref.height = `${iframe.body.clientHeight}px`;

            // overflowY = 'hidden' forces Safari to obey the iframe height without adding a scrollbar
            //       without it, it adds the scrollbar which reduces the width + increases the height which
            //       removes the scrollbar which increases the width + reduces the height which
            //       ... yeah it's basically an infinite loop of pure pain
            iframe.body.style.overflowY = 'hidden';
        }
    });

    tryGetIframe = (): { ref: HTMLIFrameElement; body: HTMLElement } | void => {
        try {
            // we dive into the iframe's body – we can do this as the article contents are proxied through servicedesk
            // the body is the right container on all browsers – documentElement is wrong on Safari, hence its not being used
            // if this throws an error, it means we don't have permission to access the document body
            if (
                this.iFrameRef &&
                this.iFrameRef.contentWindow &&
                this.iFrameRef.contentWindow.document &&
                this.iFrameRef.contentWindow.document.body
            ) {
                return {
                    ref: this.iFrameRef,
                    body: this.iFrameRef.contentWindow.document.body,
                };
            }
        } catch (e) {
            // report the error to sentry and stop polling (to prevent a flood of errors coming in)
            reportError(e, {
                message: `Failed to resize iframe. Origin: ${window.location.origin}; Source: ${this.getIframeSrc()}`,
            });
            this.removeResizePolling();
        }
        return undefined;
    };

    componentWillUnmount() {
        this.removeResizeListener();
        this.removeResizePolling();
    }

    setIFrameRef = (ref: HTMLIFrameElement | null) => {
        this.iFrameRef = ref;
        this.resizeIFrameHeight();
        this.beginResizePolling();
    };

    onLoaded = () => {
        this.props.onIFrameLoaded && this.props.onIFrameLoaded();
        if (this.isSameOrigin()) {
            this.resizeIFrameHeight();

            this.removeResizeListener();
            window.addEventListener('resize', this.resizeIFrameHeight);
            this.removeResizeListener = () => window.removeEventListener('resize', this.resizeIFrameHeight);

            this.removeResizePolling();
            this.beginResizePolling();
        }
    };

    beginResizePolling = () => {
        if (this.isResizePollingEnabled()) {
            this.removeResizePolling();
            const intervalId = setInterval(this.resizeIFrameHeight, this.props.resizePollInterval);
            this.removeResizePolling = () => clearInterval(intervalId);
        }
    };

    getIframeSrc = (): string => {
        return this.iFrameRef && typeof this.iFrameRef.src === 'string' ? this.iFrameRef.src : this.props.src;
    };

    isSameOrigin = (): boolean => {
        const { sameOriginResize } = this.props;
        return sameOriginResize && this.getIframeSrc().startsWith(window.location.origin);
    };

    isResizePollingEnabled = (): boolean => {
        const { resizePoll, resizePollInterval } = this.props;
        return this.isSameOrigin() && resizePoll && resizePollInterval > 0;
    };

    render() {
        const { src, title } = this.props;

        return <IFrame title={title} innerRef={this.setIFrameRef} key={src} src={src} onLoad={this.onLoaded} />;
    }
}

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const IFrame = styled.iframe`
    border: none;
    margin: 0;
    width: 100%;
`;
